import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Typography, Grid, Container } from '@mui/material';
import { Form } from 'react-final-form';

import { formValidationSchema, ZIP_CODE_LENGTH } from './formValidationSchema';
import { CardNumberField } from '../../components/Fields/CardNumberField/CardNumberField';
import { CardExpirationDateField } from '../../components/Fields/CardExpirationDateField/CardExpirationDateField';
import { CardCVVField } from '../../components/CardCVVField';
import { ShowErrorFunc, TextField } from '../../components/TextField';
import { IAddCardFormFields } from './types';
import { getMaxLengthParser } from '../../lib/form/utils';
import { ADD_CARD_TEST_IDS } from './testIds';
import { SizingMultiplier } from '../../theme/constants';
import { Button } from '../../components/Button';
import { buttonTextMessages } from '../../modules/localization/button-text.messages';

export interface IAddCardProps {
  cardholderName: string;
  onSubmit(values: IAddCardFormFields): Promise<void>;
}

const zipCodeParser = getMaxLengthParser(ZIP_CODE_LENGTH);

export const AddCard: React.FC<IAddCardProps> = (props) => {
  const { formatMessage } = useIntl();
  const showCardNumberError = useShowCardNumberError();
  const { cardholderName, onSubmit } = props;

  const initialValues = useMemo(
    () => ({
      cardholderName,
      cardNumber: '',
      expirationDate: '',
      cvv: '',
      billingZip: '',
    }),
    [cardholderName],
  );

  return (
    <Container sx={{ pt: SizingMultiplier.lg }}>
      <Typography variant="h4" align="center" mb={SizingMultiplier.md}>
        <FormattedMessage id="addCard:title" defaultMessage="Add Card" description="Title for Add Card page" />
      </Typography>
      <Form<IAddCardFormFields>
        validate={formValidationSchema}
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, hasValidationErrors, dirty }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CardNumberField
                  variant="outlined"
                  name="cardNumber"
                  fullWidth
                  label={formatMessage({
                    id: 'addCard:cardNumberFieldLabel',
                    defaultMessage: 'Card Number',
                    description: 'Card Number field label',
                  })}
                  showError={showCardNumberError}
                />
              </Grid>
              <Grid item xs={6}>
                <CardExpirationDateField
                  variant="outlined"
                  name="expirationDate"
                  fullWidth
                  label={formatMessage({
                    id: 'addCard:expirationDateLabel',
                    defaultMessage: 'Expiration Date',
                    description: 'Expiration Date field label',
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <CardCVVField
                  variant="outlined"
                  name="cvv"
                  fullWidth
                  label={formatMessage({
                    id: 'addCard:cvvLabel',
                    defaultMessage: 'CVV',
                    description: 'CVV field label',
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  name="cardholderName"
                  fullWidth
                  label={formatMessage({
                    id: 'addCard:cardholderNameLabel',
                    defaultMessage: 'Cardholder Name',
                    description: 'Cardholder Name field label',
                  })}
                  helperText={formatMessage({
                    id: 'addCard:cardholderNameHelperText',
                    defaultMessage: 'Enter the name as it appears on card',
                    description: 'Cardholder Name helper text',
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  name="billingZip"
                  type="number"
                  inputProps={{
                    inputMode: 'numeric',
                    autoComplete: 'on',
                    autocompletetype: 'cc-zip',
                    pattern: '\\d*',
                    'data-testid': ADD_CARD_TEST_IDS.ZIP_CODE_FIELD,
                  }}
                  fieldProps={{
                    parse: zipCodeParser,
                  }}
                  fullWidth
                  label={formatMessage({
                    id: 'addCard:billingZipLabel',
                    defaultMessage: 'Billing ZIP',
                    description: 'Billing ZIP field label',
                  })}
                />
              </Grid>
              <Grid item xs={12} textAlign="center">
                <Button disabled={hasValidationErrors || !dirty} type="submit">
                  <FormattedMessage {...buttonTextMessages['button-text:submit']} />
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      />
    </Container>
  );
};

/**
 * A more intuitive way to show validation errors on the Card Number input:
 * - If the input has never been touched or is valid, then do not show the error message.
 * - If the user is currently changing the card number, then do not show the error message.
 * - Otherwise, the value is invalid and input is blurred or focussed but unchanged, so show the error message.
 */
const useShowCardNumberError = () => {
  /**
   * This function is called during a render phase; triggering a state change would throw a React error here (can't render inside a render.)
   * Instead, use a ref value to capture the previous input value for change detection.
   */
  const prevValue = React.useRef();

  return React.useCallback<ShowErrorFunc>(({ meta: { valid, active, touched }, value }) => {
    const changed = prevValue.current !== value;
    if (changed) {
      prevValue.current = value;
    }

    if (valid || !touched) {
      return false;
    }

    if (active && changed) {
      return false;
    }

    return true;
  }, []);
};
